import React, { useState, useEffect } from 'react';
import { updatePassword, signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';

interface PasswordRequirement {
    label: string;
    validator: (password: string) => boolean;
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
    { label: 'At least 8 characters', validator: (p) => p.length >= 8 },
    { label: 'At least one uppercase letter', validator: (p) => /[A-Z]/.test(p) },
    { label: 'At least one lowercase letter', validator: (p) => /[a-z]/.test(p) },
    { label: 'At least one number', validator: (p) => /[0-9]/.test(p) },
    { label: 'At least one special character (!@#$%^&*)', validator: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
];

interface ChangePasswordProps {
    onPasswordChanged: () => void;
}

const ChangePassword: React.FC<ChangePasswordProps> = ({ onPasswordChanged }) => {
    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

    const validatePassword = (password: string): boolean => {
        return PASSWORD_REQUIREMENTS.every(req => req.validator(password));
    };

    const getPasswordStrength = (password: string): { level: number; label: string; color: string } => {
        const metRequirements = PASSWORD_REQUIREMENTS.filter(req => req.validator(password)).length;
        const total = PASSWORD_REQUIREMENTS.length;
        
        if (metRequirements === 0) return { level: 0, label: '', color: '#e0e0e0' };
        if (metRequirements <= 2) return { level: 1, label: 'Weak', color: '#f44336' };
        if (metRequirements <= 3) return { level: 2, label: 'Fair', color: '#ff9800' };
        if (metRequirements <= 4) return { level: 3, label: 'Good', color: '#2196f3' };
        return { level: 4, label: 'Strong', color: '#4caf50' };
    };

    const passwordStrength = getPasswordStrength(newPassword);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Validate passwords match
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        // Validate password requirements
        if (!validatePassword(newPassword)) {
            setError('Password does not meet all requirements');
            return;
        }

        setIsLoading(true);

        try {
            const currentUser = auth.currentUser;
            if (!currentUser) {
                throw new Error('No user is currently signed in');
            }

            // Update password in Firebase Auth
            await updatePassword(currentUser, newPassword);

            // Update the therapist record to mark password as changed
            const therapistRef = doc(db, 'Therapists', currentUser.uid);
            await updateDoc(therapistRef, {
                mustChangePassword: false
            });

            console.log('Password changed successfully');
            
            // Notify parent component that password was changed
            onPasswordChanged();

        } catch (error: any) {
            console.error('Error changing password:', error);
            
            // Handle specific Firebase Auth errors
            if (error.code === 'auth/requires-recent-login') {
                setError('For security reasons, please log out and log in again before changing your password.');
            } else if (error.code === 'auth/weak-password') {
                setError('Password is too weak. Please choose a stronger password.');
            } else {
                setError(error.message || 'Failed to change password. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    return (
        <div style={styles.background}>
            <div style={styles.container}>
                <div style={styles.header}>
                    <h2 style={styles.title}>Create Your Password</h2>
                    <p style={styles.subtitle}>
                        For security, please create a new password to replace the temporary one provided.
                    </p>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* New Password Field */}
                    <div style={styles.inputGroup}>
                        <label style={styles.label}>New Password</label>
                        <div style={styles.passwordInputWrapper}>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder='Enter new password'
                                style={styles.input}
                                disabled={isLoading}
                            />
                            <button
                                type='button'
                                onClick={() => setShowPassword(!showPassword)}
                                style={styles.showPasswordButton}
                                disabled={isLoading}
                            >
                                {showPassword ? 'Hide' : 'Show'}
                            </button>
                        </div>
                    </div>

                    {/* Password Strength Indicator */}
                    {newPassword.length > 0 && (
                        <div style={styles.strengthContainer}>
                            <div style={styles.strengthBarBackground}>
                                <div 
                                    style={{
                                        ...styles.strengthBar,
                                        width: `${(passwordStrength.level / 4) * 100}%`,
                                        backgroundColor: passwordStrength.color
                                    }}
                                />
                            </div>
                            <span style={{ ...styles.strengthLabel, color: passwordStrength.color }}>
                                {passwordStrength.label}
                            </span>
                        </div>
                    )}

                    {/* Password Requirements */}
                    <div style={styles.requirementsContainer}>
                        <p style={styles.requirementsTitle}>Password must contain:</p>
                        <ul style={styles.requirementsList}>
                            {PASSWORD_REQUIREMENTS.map((req, index) => {
                                const isMet = req.validator(newPassword);
                                return (
                                    <li 
                                        key={index} 
                                        style={{
                                            ...styles.requirementItem,
                                            color: newPassword.length === 0 
                                                ? '#666' 
                                                : isMet 
                                                    ? '#4caf50' 
                                                    : '#f44336'
                                        }}
                                    >
                                        <span style={styles.requirementIcon}>
                                            {newPassword.length === 0 ? '○' : isMet ? '✓' : '✗'}
                                        </span>
                                        {req.label}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    {/* Confirm Password Field */}
                    <div style={styles.inputGroup}>
                        <label style={styles.label}>Confirm Password</label>
                        <div style={styles.passwordInputWrapper}>
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder='Confirm new password'
                                style={{
                                    ...styles.input,
                                    borderColor: confirmPassword.length > 0 && confirmPassword !== newPassword 
                                        ? '#f44336' 
                                        : confirmPassword.length > 0 && confirmPassword === newPassword
                                            ? '#4caf50'
                                            : '#ccc'
                                }}
                                disabled={isLoading}
                            />
                            <button
                                type='button'
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                style={styles.showPasswordButton}
                                disabled={isLoading}
                            >
                                {showConfirmPassword ? 'Hide' : 'Show'}
                            </button>
                        </div>
                        {confirmPassword.length > 0 && confirmPassword !== newPassword && (
                            <p style={styles.matchError}>Passwords do not match</p>
                        )}
                        {confirmPassword.length > 0 && confirmPassword === newPassword && (
                            <p style={styles.matchSuccess}>Passwords match ✓</p>
                        )}
                    </div>

                    {/* Submit Button */}
                    <button 
                        type='submit' 
                        style={{
                            ...styles.button,
                            opacity: isLoading || !validatePassword(newPassword) || newPassword !== confirmPassword ? 0.6 : 1,
                            cursor: isLoading || !validatePassword(newPassword) || newPassword !== confirmPassword ? 'not-allowed' : 'pointer'
                        }}
                        disabled={isLoading || !validatePassword(newPassword) || newPassword !== confirmPassword}
                    >
                        {isLoading ? 'Creating Password...' : 'Create Password'}
                    </button>

                    {/* Error Message */}
                    {error && <p style={styles.error}>{error}</p>}

                    {/* Logout Link */}
                    <div style={styles.logoutContainer}>
                        <button 
                            type='button' 
                            onClick={handleLogout} 
                            style={styles.logoutButton}
                            disabled={isLoading}
                        >
                            Use a different account
                        </button>
                    </div>
                </form>
            </div>

            {/* Footer */}
            <footer style={styles.footer}>
                <div style={styles.footerText}>
                    © 2025 BeneTalk LTD. All Rights Reserved.
                </div>
                <a href='http://superpenguin.com' target='_blank' rel='noopener noreferrer'>
                    <img
                        src={require('./assets/superpenguin-logo.webp')}
                        alt='SuperPenguin Logo'
                        style={styles.footerImage}
                    />
                </a>
            </footer>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    background: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundImage: `url(${require('./assets/loginback.png')})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        padding: '20px',
    },
    container: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        padding: '30px',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
        width: '100%',
        maxWidth: '420px',
        zIndex: 1,
    },
    header: {
        textAlign: 'center',
        marginBottom: '24px',
    },
    title: {
        margin: '0 0 8px 0',
        color: '#333',
        fontSize: '24px',
        fontWeight: '600',
    },
    subtitle: {
        margin: 0,
        color: '#666',
        fontSize: '14px',
        lineHeight: '1.5',
    },
    inputGroup: {
        marginBottom: '16px',
    },
    label: {
        display: 'block',
        marginBottom: '6px',
        color: '#333',
        fontSize: '14px',
        fontWeight: '500',
    },
    passwordInputWrapper: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
    },
    input: {
        width: '100%',
        padding: '12px',
        paddingRight: '60px',
        borderRadius: '6px',
        border: '1px solid #ccc',
        boxSizing: 'border-box',
        fontSize: '14px',
        transition: 'border-color 0.2s ease',
    },
    showPasswordButton: {
        position: 'absolute',
        right: '8px',
        background: 'none',
        border: 'none',
        color: '#666',
        cursor: 'pointer',
        fontSize: '12px',
        padding: '4px 8px',
    },
    strengthContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '16px',
    },
    strengthBarBackground: {
        flex: 1,
        height: '4px',
        backgroundColor: '#e0e0e0',
        borderRadius: '2px',
        overflow: 'hidden',
    },
    strengthBar: {
        height: '100%',
        transition: 'width 0.3s ease, background-color 0.3s ease',
        borderRadius: '2px',
    },
    strengthLabel: {
        fontSize: '12px',
        fontWeight: '500',
        minWidth: '50px',
    },
    requirementsContainer: {
        backgroundColor: '#f8f9fa',
        padding: '12px 16px',
        borderRadius: '6px',
        marginBottom: '16px',
    },
    requirementsTitle: {
        margin: '0 0 8px 0',
        fontSize: '12px',
        color: '#666',
        fontWeight: '500',
    },
    requirementsList: {
        margin: 0,
        padding: 0,
        listStyle: 'none',
    },
    requirementItem: {
        fontSize: '12px',
        marginBottom: '4px',
        display: 'flex',
        alignItems: 'center',
        transition: 'color 0.2s ease',
    },
    requirementIcon: {
        marginRight: '8px',
        width: '14px',
        textAlign: 'center',
    },
    matchError: {
        color: '#f44336',
        fontSize: '12px',
        margin: '4px 0 0 0',
    },
    matchSuccess: {
        color: '#4caf50',
        fontSize: '12px',
        margin: '4px 0 0 0',
    },
    button: {
        width: '100%',
        padding: '14px',
        marginTop: '8px',
        backgroundColor: '#4CAF50',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '500',
        transition: 'background-color 0.2s ease, opacity 0.2s ease',
    },
    error: {
        color: '#f44336',
        marginTop: '12px',
        textAlign: 'center',
        fontSize: '14px',
    },
    logoutContainer: {
        marginTop: '16px',
        textAlign: 'center',
    },
    logoutButton: {
        background: 'none',
        border: 'none',
        color: '#666',
        cursor: 'pointer',
        fontSize: '13px',
        textDecoration: 'underline',
    },
    footer: {
        backgroundColor: '#fff',
        padding: '10px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        position: 'absolute',
        bottom: 0,
    },
    footerText: {
        paddingLeft: '10px',
        fontSize: '12px',
        color: '#000',
    },
    footerImage: {
        width: '150px',
        paddingRight: '10px',
    },
};

export default ChangePassword;







